'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RentalPropertyCalculator() {
  const t = useTranslations("tools.rentalPropertyCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [inputs, setInputs] = useState({
    purchasePrice: 200000,
    useLoan: true,
    downPayment: 20,
    interestRate: 6,
    loanTerm: 30,
    closingCost: 6000,
    needRepair: false,
    repairCost: 20000,
    afterRepairValue: 260000,
    monthlyRent: 2000,
    rentIncrease: 3,
    otherIncome: 0,
    vacancyRate: 5,
    managementFee: 0,
    propertyTax: 3000,
    insurance: 1200,
    hoaFee: 0,
    maintenance: 2000,
    otherCosts: 500,
    expenseIncrease: 3,
    holdingLength: 20,
    knowSellPrice: false,
    appreciation: 3,
    sellPrice: 400000,
    costToSell: 8,
  });

  const [results, setResults] = useState<any>(null);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#059669',
    danger: '#dc2626',
  };

  const calculateIRR = (cashFlows: number[]) => {
    const maxIterations = 1000;
    const tolerance = 0.00001;
    let guess = 0.1;

    for (let i = 0; i < maxIterations; i++) {
      let npv = 0;
      let dnpv = 0;

      for (let j = 0; j < cashFlows.length; j++) {
        npv += cashFlows[j] / Math.pow(1 + guess, j);
        dnpv -= j * cashFlows[j] / Math.pow(1 + guess, j + 1);
      }

      const newGuess = guess - npv / dnpv;

      if (Math.abs(newGuess - guess) < tolerance) {
        return newGuess;
      }

      guess = newGuess;
    }

    return guess;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const purchasePrice = inputs.purchasePrice;
    const useLoan = inputs.useLoan;
    const downPaymentPct = inputs.downPayment / 100;
    const interestRate = inputs.interestRate / 100;
    const loanTerm = inputs.loanTerm;
    const closingCost = inputs.closingCost;
    const needRepair = inputs.needRepair;
    const repairCost = needRepair ? inputs.repairCost : 0;
    const afterRepairValue = needRepair ? inputs.afterRepairValue : purchasePrice;

    const monthlyRent = inputs.monthlyRent;
    const rentIncrease = inputs.rentIncrease / 100;
    const otherIncome = inputs.otherIncome;
    const vacancyRate = inputs.vacancyRate / 100;
    const managementFee = inputs.managementFee / 100;

    const propertyTax = inputs.propertyTax;
    const insurance = inputs.insurance;
    const hoaFee = inputs.hoaFee;
    const maintenance = inputs.maintenance;
    const otherCosts = inputs.otherCosts;
    const expenseIncrease = inputs.expenseIncrease / 100;

    const holdingLength = inputs.holdingLength;
    const knowSellPrice = inputs.knowSellPrice;
    const appreciation = inputs.appreciation / 100;
    const sellPrice = knowSellPrice ? inputs.sellPrice :
      afterRepairValue * Math.pow(1 + appreciation, holdingLength);
    const costToSell = inputs.costToSell / 100;

    const downPayment = useLoan ? purchasePrice * downPaymentPct : purchasePrice;
    const loanAmount = useLoan ? purchasePrice - downPayment : 0;
    const totalInvestment = downPayment + closingCost + repairCost;

    let monthlyMortgage = 0;
    if (useLoan && loanAmount > 0) {
      const monthlyRate = interestRate / 12;
      const numPayments = loanTerm * 12;
      monthlyMortgage = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);
    }

    const yearlyData = [];
    let cumulativeCashFlow = 0;
    const cashFlows = [-totalInvestment];

    for (let year = 1; year <= holdingLength; year++) {
      const yearRent = monthlyRent * Math.pow(1 + rentIncrease, year - 1);
      const yearOtherIncome = otherIncome * Math.pow(1 + rentIncrease, year - 1);
      const grossIncome = (yearRent + yearOtherIncome) * 12;
      const vacancyLoss = grossIncome * vacancyRate;
      const effectiveIncome = grossIncome - vacancyLoss;
      const managementCost = effectiveIncome * managementFee;

      const yearExpenses = (propertyTax + insurance + hoaFee + maintenance + otherCosts) *
        Math.pow(1 + expenseIncrease, year - 1);

      const totalExpenses = yearExpenses + managementCost;
      const noi = effectiveIncome - totalExpenses;
      const mortgagePayment = monthlyMortgage * 12;
      const cashFlow = noi - mortgagePayment;

      cumulativeCashFlow += cashFlow;
      cashFlows.push(cashFlow);

      yearlyData.push({
        year: year,
        income: effectiveIncome,
        expenses: totalExpenses,
        mortgage: mortgagePayment,
        cashFlow: cashFlow,
        cumulative: cumulativeCashFlow
      });
    }

    const year1Data = yearlyData[0];
    const year1NOI = year1Data.income - year1Data.expenses;
    const capRate = (year1NOI / purchasePrice) * 100;
    const cashOnCash = (year1Data.cashFlow / totalInvestment) * 100;

    let remainingBalance = loanAmount;
    if (useLoan && loanAmount > 0) {
      const monthlyRate = interestRate / 12;
      const numPayments = loanTerm * 12;
      const paymentsToDate = holdingLength * 12;
      if (paymentsToDate < numPayments) {
        remainingBalance = loanAmount * (Math.pow(1 + monthlyRate, numPayments) - Math.pow(1 + monthlyRate, paymentsToDate)) /
          (Math.pow(1 + monthlyRate, numPayments) - 1);
      } else {
        remainingBalance = 0;
      }
    }

    const sellingCost = sellPrice * costToSell;
    const netSaleProceeds = sellPrice - sellingCost - remainingBalance;
    cashFlows[holdingLength] += netSaleProceeds;

    const totalProfit = cumulativeCashFlow + netSaleProceeds - totalInvestment;
    const irr = calculateIRR(cashFlows);

    setResults({
      totalInvestment,
      monthlyCashFlow: year1Data.cashFlow / 12,
      capRate,
      cashOnCash,
      totalProfit,
      irr: irr * 100,
      holdingLength,
      yearlyData,
    });

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    {},
    { maxWidth: '1200px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: screenSize === 'mobile' ? '1.5rem' : '2rem',
    textAlign: 'center' as const,
  };

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const formStyle = {
    padding: screenSize === 'mobile' ? '1.5rem' : '2rem',
  };

  const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: screenSize === 'mobile' ? '1.5rem' : '2rem',
    marginBottom: screenSize === 'mobile' ? '1.5rem' : '2rem',
  };

  const formSectionStyle = {
    background: '#f8fafc',
    padding: screenSize === 'mobile' ? '1.25rem' : '1.5rem',
    borderRadius: '0.75rem',
    border: '2px solid #e2e8f0',
  };

  const formSectionH3Style = {
    color: '#2563eb',
    fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem',
    margin: '0 0 1.5rem 0',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #2563eb',
  };

  const formGroupStyle = {
    marginBottom: '1.25rem',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#334155',
    fontWeight: 600,
    fontSize: '0.95rem',
  };

  const inputStyle = {
    width: '100%',
    padding: screenSize === 'mobile' ? '0.75rem' : '0.875rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: screenSize === 'mobile' ? '1rem' : '1.125rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  const inputGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const inputPrefixStyle = {
    color: '#64748b',
    fontWeight: 600,
  };

  const inputSuffixStyle = {
    color: '#64748b',
    fontSize: '0.9rem',
  };

  const radioGroupStyle = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    fontWeight: 500,
  };

  const conditionalSectionStyle = {
    marginLeft: screenSize === 'mobile' ? '1rem' : '1.5rem',
    marginTop: '1rem',
    paddingLeft: '1rem',
    borderLeft: '3px solid #7c3aed',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 700,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'block',
      margin: '2rem auto 0',
      boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
      width: '100%',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem', width: 'auto' },
    { padding: '1.25rem 3rem', fontSize: '1.25rem', width: 'auto' }
  );

  const resultsStyle = {
    padding: screenSize === 'mobile' ? '1.5rem' : '2rem',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    borderTop: '3px solid #10b981',
  };

  const resultsGridStyle = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem',
  };

  const resultCardStyle = {
    background: 'white',
    padding: screenSize === 'mobile' ? '1.25rem' : '1.5rem',
    borderRadius: '0.75rem',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
    borderLeft: '4px solid #2563eb',
  };

  const resultLabelStyle = {
    display: 'block',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const resultValueStyle = {
    display: 'block',
    color: '#1e293b',
    fontSize: screenSize === 'mobile' ? '1.5rem' : '1.75rem',
    fontWeight: 700,
  };

  const resultDescriptionStyle = {
    color: '#64748b',
    fontSize: '0.85rem',
    marginTop: '0.5rem',
  };

  const yearlyBreakdownStyle = {
    background: 'white',
    padding: screenSize === 'mobile' ? '1.25rem' : '1.5rem',
    borderRadius: '0.75rem',
    marginTop: '1.5rem',
  };

  const breakdownTableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: screenSize === 'mobile' ? '0.8rem' : '0.9rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' }
  );

  const referenceStyle = {
    marginBottom: '0.75rem',
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem',
    wordBreak: 'break-word' as const,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={{ margin: 0, fontSize: screenSize === 'mobile' ? '1.5rem' : '2rem', fontWeight: 700 }}>
              {t("toolTitle")}
            </h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGridStyle}>
              {/* Purchase Details */}
              <div style={formSectionStyle}>
                <h3 style={formSectionH3Style}>{t("purchaseDetailsTitle")}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="purchase-price">{t("purchasePrice")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="purchase-price"
                      value={inputs.purchasePrice}
                      onChange={(e) => handleInputChange('purchasePrice', parseFloat(e.target.value))}
                      min="0"
                      step="1000"
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("useLoan")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="use-loan"
                        checked={inputs.useLoan}
                        onChange={() => handleInputChange('useLoan', true)}
                      />
                      {t("yes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="use-loan"
                        checked={!inputs.useLoan}
                        onChange={() => handleInputChange('useLoan', false)}
                      />
                      {t("no")}
                    </label>
                  </div>

                  {inputs.useLoan && (
                    <div style={conditionalSectionStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="down-payment">{t("downPayment")}</label>
                        <div style={inputGroupStyle}>
                          <input
                            type="number"
                            id="down-payment"
                            value={inputs.downPayment}
                            onChange={(e) => handleInputChange('downPayment', parseFloat(e.target.value))}
                            min="0"
                            max="100"
                            step="0.1"
                            style={inputStyle}
                          />
                          <span style={inputSuffixStyle}>%</span>
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="interest-rate">{t("interestRate")}</label>
                        <div style={inputGroupStyle}>
                          <input
                            type="number"
                            id="interest-rate"
                            value={inputs.interestRate}
                            onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
                            min="0"
                            max="30"
                            step="0.1"
                            style={inputStyle}
                          />
                          <span style={inputSuffixStyle}>%</span>
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="loan-term">{t("loanTerm")}</label>
                        <div style={inputGroupStyle}>
                          <input
                            type="number"
                            id="loan-term"
                            value={inputs.loanTerm}
                            onChange={(e) => handleInputChange('loanTerm', parseFloat(e.target.value))}
                            min="1"
                            max="50"
                            style={inputStyle}
                          />
                          <span style={inputSuffixStyle}>{t("years")}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="closing-cost">{t("closingCost")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="closing-cost"
                      value={inputs.closingCost}
                      onChange={(e) => handleInputChange('closingCost', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("needRepairs")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="need-repair"
                        checked={inputs.needRepair}
                        onChange={() => handleInputChange('needRepair', true)}
                      />
                      {t("yes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="need-repair"
                        checked={!inputs.needRepair}
                        onChange={() => handleInputChange('needRepair', false)}
                      />
                      {t("no")}
                    </label>
                  </div>

                  {inputs.needRepair && (
                    <div style={conditionalSectionStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="repair-cost">{t("repairCost")}</label>
                        <div style={inputGroupStyle}>
                          <span style={inputPrefixStyle}>$</span>
                          <input
                            type="number"
                            id="repair-cost"
                            value={inputs.repairCost}
                            onChange={(e) => handleInputChange('repairCost', parseFloat(e.target.value))}
                            min="0"
                            step="1000"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="after-repair-value">{t("afterRepairValue")}</label>
                        <div style={inputGroupStyle}>
                          <span style={inputPrefixStyle}>$</span>
                          <input
                            type="number"
                            id="after-repair-value"
                            value={inputs.afterRepairValue}
                            onChange={(e) => handleInputChange('afterRepairValue', parseFloat(e.target.value))}
                            min="0"
                            step="1000"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Income */}
              <div style={formSectionStyle}>
                <h3 style={formSectionH3Style}>{t("incomeTitle")}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="monthly-rent">{t("monthlyRent")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="monthly-rent"
                      value={inputs.monthlyRent}
                      onChange={(e) => handleInputChange('monthlyRent', parseFloat(e.target.value))}
                      min="0"
                      step="50"
                      required
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="rent-increase">{t("annualRentIncrease")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="rent-increase"
                      value={inputs.rentIncrease}
                      onChange={(e) => handleInputChange('rentIncrease', parseFloat(e.target.value))}
                      min="0"
                      max="20"
                      step="0.1"
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="other-income">{t("otherMonthlyIncome")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="other-income"
                      value={inputs.otherIncome}
                      onChange={(e) => handleInputChange('otherIncome', parseFloat(e.target.value))}
                      min="0"
                      step="50"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="vacancy-rate">{t("vacancyRate")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="vacancy-rate"
                      value={inputs.vacancyRate}
                      onChange={(e) => handleInputChange('vacancyRate', parseFloat(e.target.value))}
                      min="0"
                      max="100"
                      step="0.1"
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="management-fee">{t("managementFee")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="management-fee"
                      value={inputs.managementFee}
                      onChange={(e) => handleInputChange('managementFee', parseFloat(e.target.value))}
                      min="0"
                      max="100"
                      step="0.1"
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>

              {/* Operating Expenses */}
              <div style={formSectionStyle}>
                <h3 style={formSectionH3Style}>{t("operatingExpensesTitle")}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="property-tax">{t("annualPropertyTax")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="property-tax"
                      value={inputs.propertyTax}
                      onChange={(e) => handleInputChange('propertyTax', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="insurance">{t("annualInsurance")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="insurance"
                      value={inputs.insurance}
                      onChange={(e) => handleInputChange('insurance', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="hoa-fee">{t("annualHoaFee")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="hoa-fee"
                      value={inputs.hoaFee}
                      onChange={(e) => handleInputChange('hoaFee', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="maintenance">{t("annualMaintenance")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="maintenance"
                      value={inputs.maintenance}
                      onChange={(e) => handleInputChange('maintenance', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="other-costs">{t("annualOtherCosts")}</label>
                  <div style={inputGroupStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="other-costs"
                      value={inputs.otherCosts}
                      onChange={(e) => handleInputChange('otherCosts', parseFloat(e.target.value))}
                      min="0"
                      step="100"
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="expense-increase">{t("annualExpenseIncrease")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="expense-increase"
                      value={inputs.expenseIncrease}
                      onChange={(e) => handleInputChange('expenseIncrease', parseFloat(e.target.value))}
                      min="0"
                      max="20"
                      step="0.1"
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>

              {/* Sale Details */}
              <div style={formSectionStyle}>
                <h3 style={formSectionH3Style}>{t("saleDetailsTitle")}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="holding-length">{t("holdingLength")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="holding-length"
                      value={inputs.holdingLength}
                      onChange={(e) => handleInputChange('holdingLength', parseInt(e.target.value))}
                      min="1"
                      max="50"
                      required
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>{t("years")}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("knowSellPrice")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="know-sell-price"
                        checked={inputs.knowSellPrice}
                        onChange={() => handleInputChange('knowSellPrice', true)}
                      />
                      {t("yes")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="know-sell-price"
                        checked={!inputs.knowSellPrice}
                        onChange={() => handleInputChange('knowSellPrice', false)}
                      />
                      {t("no")}
                    </label>
                  </div>

                  {!inputs.knowSellPrice && (
                    <div style={conditionalSectionStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="appreciation">{t("annualAppreciation")}</label>
                        <div style={inputGroupStyle}>
                          <input
                            type="number"
                            id="appreciation"
                            value={inputs.appreciation}
                            onChange={(e) => handleInputChange('appreciation', parseFloat(e.target.value))}
                            min="-10"
                            max="20"
                            step="0.1"
                            style={inputStyle}
                          />
                          <span style={inputSuffixStyle}>%</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {inputs.knowSellPrice && (
                    <div style={conditionalSectionStyle}>
                      <div style={formGroupStyle}>
                        <label style={labelStyle} htmlFor="sell-price">{t("sellPrice")}</label>
                        <div style={inputGroupStyle}>
                          <span style={inputPrefixStyle}>$</span>
                          <input
                            type="number"
                            id="sell-price"
                            value={inputs.sellPrice}
                            onChange={(e) => handleInputChange('sellPrice', parseFloat(e.target.value))}
                            min="0"
                            step="1000"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cost-to-sell">{t("costToSell")}</label>
                  <div style={inputGroupStyle}>
                    <input
                      type="number"
                      id="cost-to-sell"
                      value={inputs.costToSell}
                      onChange={(e) => handleInputChange('costToSell', parseFloat(e.target.value))}
                      min="0"
                      max="20"
                      step="0.1"
                      style={inputStyle}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results">
              <h3 style={{ color: colors.success, fontSize: screenSize === 'mobile' ? '1.5rem' : '1.75rem', margin: '0 0 1.5rem 0', textAlign: 'center' }}>
                {t("resultsTitle")}
              </h3>

              <div style={resultsGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("totalInvestment")}</span>
                  <span style={resultValueStyle}>
                    ${results.totalInvestment.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("monthlyCashFlow")}</span>
                  <span style={{ ...resultValueStyle, color: results.monthlyCashFlow >= 0 ? colors.success : colors.danger }}>
                    ${results.monthlyCashFlow.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("capRate")}</span>
                  <span style={resultValueStyle}>{results.capRate.toFixed(2)}%</span>
                  <span style={resultDescriptionStyle}>{t("capRateDescription")}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("cashOnCash")}</span>
                  <span style={resultValueStyle}>{results.cashOnCash.toFixed(2)}%</span>
                  <span style={resultDescriptionStyle}>{t("cashOnCashDescription")}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("totalProfit")}</span>
                  <span style={{ ...resultValueStyle, color: results.totalProfit >= 0 ? colors.success : colors.danger }}>
                    ${results.totalProfit.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </span>
                  <span style={resultDescriptionStyle}>
                    {t("totalProfitDescription")} {results.holdingLength} {t("years")}
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t("irr")}</span>
                  <span style={resultValueStyle}>{results.irr.toFixed(2)}%</span>
                  <span style={resultDescriptionStyle}>{t("irrDescription")}</span>
                </div>
              </div>

              <div style={yearlyBreakdownStyle}>
                <h4 style={{ color: '#2563eb', margin: '0 0 1rem 0', fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem' }}>
                  {t("yearlyBreakdown")}
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={breakdownTableStyle}>
                    <thead>
                      <tr>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("year")}
                        </th>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("rentalIncome")}
                        </th>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("operatingExpenses")}
                        </th>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("mortgagePayment")}
                        </th>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("cashFlow")}
                        </th>
                        <th style={{ background: '#f1f5f9', padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                          {t("cumulativeCashFlow")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.yearlyData.map((data: any) => (
                        <tr key={data.year} style={{ transition: 'background 0.2s' }}>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                            {data.year}
                          </td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                            ${data.income.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                            ${data.expenses.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
                            ${data.mortgage.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0', color: data.cashFlow >= 0 ? colors.success : colors.danger }}>
                            ${data.cashFlow.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                          <td style={{ padding: screenSize === 'mobile' ? '0.5rem' : '0.75rem', borderBottom: '1px solid #e2e8f0', color: data.cumulative >= 0 ? colors.success : colors.danger }}>
                            ${data.cumulative.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph4")}</p>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem' }}>{t("keyMetricsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>{t("noiLabel")}</strong> {t("noiDescription")}</li>
          <li style={listItemStyle}><strong>{t("capRateLabel")}</strong> {t("capRateDetailDescription")}</li>
          <li style={listItemStyle}><strong>{t("cashOnCashLabel")}</strong> {t("cashOnCashDetailDescription")}</li>
          <li style={listItemStyle}><strong>{t("irrLabel")}</strong> {t("irrDetailDescription")}</li>
          <li style={listItemStyle}><strong>{t("cashFlowLabel")}</strong> {t("cashFlowDescription")}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '2rem' }}>{t("importantConsiderationsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("consideration1")}</li>
          <li style={listItemStyle}>{t("consideration2")}</li>
          <li style={listItemStyle}>{t("consideration3")}</li>
          <li style={listItemStyle}>{t("consideration4")}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <div style={referenceStyle}>
          <a href="https://www.evernest.co/blog/why-every-investor-needs-a-rental-property-calculator-a-comprehensive-guide" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t("reference1")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.har.com/blog_116739_rental-property-cash-flow-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t("reference2")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://www.turbotenant.com/landlord-toolbox/rental-property-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t("reference3")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://join.globalvacationrentals.com/blog/rental-income-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t("reference4")}
          </a>
        </div>
        <div style={referenceStyle}>
          <a href="https://edinhart.com/rental-property-roi-calculator/" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
            {t("reference5")}
          </a>
        </div>
      </section>
    </div>
  );
}